<template>
  <Modal class="workspace-menu-settings-modal" wide @hidden="onHidden">
    <div v-if="loading" class="loading-overlay"></div>
    <h2 class="box__title">
      {{
        isEdit
          ? $t('workspaceMenuSettingsModal.titleEdit', { name: table.name })
          : $t('workspaceMenuSettingsModal.titleAdd', { name: table.name })
      }}
    </h2>
    <Error :error="error" />

    <div class="workspace-menu-settings-modal__block">
      <Checkbox v-model="form.showDetailButton">{{
        $t('workspaceMenuSettingsModal.showDetailButton')
      }}</Checkbox>
      <p class="workspace-menu-settings-modal__hint">
        {{ $t('workspaceMenuSettingsModal.showDetailHint') }}
      </p>
    </div>

    <div
      v-if="form.showDetailButton"
      class="workspace-menu-settings-modal__block workspace-menu-settings-modal__fields"
    >
      <label class="control__label">{{
        $t('workspaceMenuSettingsModal.summaryFields')
      }}</label>
      <p class="workspace-menu-settings-modal__hint">
        {{ $t('workspaceMenuSettingsModal.summaryFieldsHint') }}
      </p>
      <div class="workspace-menu-settings-modal__field-list">
        <Checkbox
          v-for="field in tableFields"
          :key="field.id"
          :checked="isSummaryFieldChecked(field.id)"
          @input="toggleSummaryField(field.id, $event)"
        >
          {{ field.name }}
        </Checkbox>
      </div>
    </div>

    <div class="separator"></div>

    <div class="workspace-menu-settings-modal__block">
      <Checkbox v-model="form.showSearch">{{
        $t('workspaceMenuSettingsModal.showSearch')
      }}</Checkbox>
    </div>

    <div class="workspace-menu-settings-modal__block">
      <Checkbox v-model="form.showGrouping">{{
        $t('workspaceMenuSettingsModal.showGrouping')
      }}</Checkbox>
    </div>

    <div class="workspace-menu-settings-modal__actions">
      <button
        class="button button--large"
        :class="{ 'button--loading': saving }"
        :disabled="saving || loading || !views.length"
        @click="save"
      >
        {{ $t('workspaceMenuSettingsModal.save') }}
      </button>
      <button
        v-if="isEdit"
        class="button button--large button--danger button--ghost"
        :class="{ 'button--loading': removing }"
        :disabled="saving || removing"
        @click="removeFromMenu"
      >
        {{ $t('workspaceMenuSettingsModal.removeFromMenu') }}
      </button>
    </div>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import error from '@baserow/modules/core/mixins/error'
import Error from '@baserow/modules/core/components/Error'
import Checkbox from '@baserow/modules/core/components/Checkbox'
import Modal from '@baserow/modules/core/components/Modal'
import FieldService from '@baserow/modules/database/services/field'
import ViewService from '@baserow/modules/database/services/view'
import WorkspaceMenuEntriesService from '@baserow/modules/core/services/workspaceMenuEntries'
import { notifyIf } from '@baserow/modules/core/utils/error'
import { populateField } from '@baserow/modules/database/store/field'

export default {
  name: 'WorkspaceTableMenuSettingsModal',
  components: { Modal, Error, Checkbox },
  mixins: [modal, error],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    /** Full menu entry when editing; null when adding */
    existingEntry: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      removing: false,
      views: [],
      tableFields: [],
      form: {
        defaultViewId: null,
        showDetailButton: false,
        summaryFieldIds: [],
        showSearch: false,
        showGrouping: false,
      },
    }
  },
  computed: {
    isEdit() {
      return this.existingEntry !== null && this.existingEntry.id != null
    },
    workspaceId() {
      return this.database.workspace.id
    },
  },
  methods: {
    viewTypeLabel(view) {
      const type = view.type || view.view_type || 'grid'
      return type
    },
    isSummaryFieldChecked(fieldId) {
      if (!this.form.summaryFieldIds.length) {
        return true
      }
      return this.form.summaryFieldIds.includes(fieldId)
    },
    toggleSummaryField(fieldId, checked) {
      const allIds = this.tableFields.map((f) => f.id)
      let next = this.form.summaryFieldIds.length
        ? [...this.form.summaryFieldIds]
        : [...allIds]
      const set = new Set(next)
      if (checked) {
        set.add(fieldId)
      } else {
        set.delete(fieldId)
      }
      next = Array.from(set)
      if (next.length === allIds.length) {
        this.form.summaryFieldIds = []
      } else {
        this.form.summaryFieldIds = next
      }
    },
    resetForm() {
      const s = (this.existingEntry && this.existingEntry.settings) || {}
      this.form = {
        defaultViewId: s.default_view_id || null,
        showDetailButton: !!s.show_detail_button,
        summaryFieldIds: Array.isArray(s.summary_field_ids)
          ? [...s.summary_field_ids]
          : [],
        showSearch: !!s.show_search,
        showGrouping: !!s.show_grouping,
      }
    },
    applyDefaultsAfterLoad() {
      if (this.views.length && !this.form.defaultViewId) {
        const grid = this.views.find((v) => (v.type || v.view_type) === 'grid')
        this.form.defaultViewId = grid ? grid.id : this.views[0].id
      }
    },
    buildSettingsPayload() {
      const allIds = this.tableFields.map((f) => f.id)
      let summaryIds = this.form.summaryFieldIds
      if (this.form.showDetailButton) {
        if (!summaryIds.length) {
          summaryIds = []
        } else if (summaryIds.length === allIds.length) {
          summaryIds = []
        }
      } else {
        summaryIds = []
      }
      return {
        default_view_id: this.form.defaultViewId,
        show_detail_button: this.form.showDetailButton,
        summary_field_ids: this.form.showDetailButton ? summaryIds : [],
        show_filters: false,
        show_search: this.form.showSearch,
        show_grouping: this.form.showGrouping,
      }
    },
    async load() {
      this.loading = true
      this.hideError()
      try {
        const [viewsRes, fieldsRes] = await Promise.all([
          ViewService(this.$client).fetchAll(this.table.id),
          FieldService(this.$client).fetchAll(this.table.id),
        ])
        this.views = viewsRes.data || []
        const rawFields = fieldsRes.data || []
        this.tableFields = rawFields.map((f) => populateField(f, this.$registry))
        this.resetForm()
        this.applyDefaultsAfterLoad()
      } catch (e) {
        this.handleError(e)
      } finally {
        this.loading = false
      }
    },
    async open() {
      await this.load()
      if (!this.views.length) {
        this.$store.dispatch('toast/error', {
          title: this.$t('workspaceMenuSettingsModal.noViewsError'),
        })
        return
      }
      this.show()
    },
    onHidden() {
      this.hideError()
    },
    async save() {
      this.saving = true
      this.hideError()
      const settings = this.buildSettingsPayload()
      const service = WorkspaceMenuEntriesService(this.$client)
      try {
        if (this.isEdit) {
          await service.update(this.workspaceId, this.existingEntry.id, {
            settings,
          })
        } else {
          await service.create(this.workspaceId, {
            target_type: 'table',
            target_id: this.table.id,
            label: this.table.name,
            settings,
          })
        }
        await this.refreshNavigationAfterMenuChange()
        this.hide()
      } catch (e) {
        notifyIf(e, 'workspace')
        this.handleError(e)
      } finally {
        this.saving = false
      }
    },
    async removeFromMenu() {
      if (!this.isEdit) {
        return
      }
      this.removing = true
      try {
        await WorkspaceMenuEntriesService(this.$client).delete(
          this.workspaceId,
          this.existingEntry.id
        )
        await this.refreshNavigationAfterMenuChange()
        this.hide()
      } catch (e) {
        notifyIf(e, 'workspace')
      } finally {
        this.removing = false
      }
    },
    async refreshNavigationAfterMenuChange() {
      const workspace = this.$store.getters['workspace/get'](this.workspaceId)
      if (workspace) {
        this.$store.commit('workspace/INVALIDATE_PERMISSIONS', {
          workspaceId: this.workspaceId,
        })
        await this.$store.dispatch('workspace/forceFetchPermissions', workspace)
      }
      this.$emit('saved')
      window.dispatchEvent(
        new CustomEvent('workspace-menu-entries-updated', {
          detail: { workspaceId: this.workspaceId },
        })
      )
    },
  },
}
</script>

<style scoped>
.workspace-menu-settings-modal__intro {
  margin: 0 0 16px;
  font-size: 14px;
  color: var(--color-neutral-600);
}

.workspace-menu-settings-modal__block {
  margin-bottom: 16px;
}

.workspace-menu-settings-modal__block--disabled {
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.workspace-menu-settings-modal__hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: 'gray'
}

.workspace-menu-settings-modal__field-list {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.workspace-menu-settings-modal__soon {
  font-size: 12px;
  color: var(--color-neutral-500);
}

.workspace-menu-settings-modal__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  z-index: 2;
}
</style>
